// ============================================================
// FILE LOCATION: src/components/TimelineBuilder/TimelineOutput.tsx
// ============================================================

/**
 * TimelineOutput Component
 *
 * Changes from previous version:
 *   - Tips bar: blue banner → neutral bar with amber dot (matches SuggestionPanel pattern)
 *   - Bullet timeline: "[Date unknown]" → clean em dash in fixed date column
 *   - Footer: "N events · timestamp" → case type label only
 */

import React, { useState } from "react";
import { TimelineOutputProps, CaseType } from "./types";

// ── Case type display labels ────────────────────────────────

const CASE_TYPE_LABELS: Record<CaseType, string> = {
  IMAGING: "Imaging authorization",
  PROCEDURE: "Procedure authorization",
  SURGERY: "Surgery authorization",
  MEDICATION: "Medication authorization",
  LEVEL_OF_CARE: "Level of care",
  DME: "DME authorization",
  THERAPY: "Therapy authorization",
  OTHER: "Authorization request",
};

// ── Date extraction helper for bullet timeline ──────────────
// Strips leading "[Date unknown] — " or "MMM YYYY — " from bullet text
// and returns { date, text } for column layout.

function parseBulletDate(bullet: string): { date: string; text: string } {
  // Match "[Date unknown] — rest"
  const unknownMatch = bullet.match(/^\[Date unknown\]\s*[—–-]\s*(.+)$/i);
  if (unknownMatch) {
    return { date: "—", text: unknownMatch[1] };
  }

  // Match "MMM YYYY — rest" or "MM/DD/YYYY — rest" etc.
  const dateMatch = bullet.match(
    /^(\w{3,9}\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{2,4}|\w{3,9}\s+\d{1,2},?\s+\d{4})\s*[—–-]\s*(.+)$/
  );
  if (dateMatch) {
    return { date: dateMatch[1], text: dateMatch[2] };
  }

  // No date prefix found — show dash
  return { date: "—", text: bullet };
}

const TimelineOutput: React.FC<TimelineOutputProps> = ({
  output,
  isLoading,
  caseType,
  onInsertToAppeal,
  onCopy,
}) => {
  const [activeTab, setActiveTab] = useState<
    "narrative" | "bullets" | "proofPoints"
  >("narrative");
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [warningsCollapsed, setWarningsCollapsed] = useState(true);

  const handleCopy = async (text: string, section: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
      onCopy?.(text);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {/* Faint header skeleton */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <div className="h-2.5 w-16 bg-slate-200/70 rounded-full animate-pulse" />
          <div className="h-2.5 w-20 bg-slate-200/50 rounded-full animate-pulse" />
          <div className="h-2.5 w-24 bg-slate-200/50 rounded-full animate-pulse" />
        </div>
        {/* Content skeleton lines */}
        <div className="px-4 py-5 space-y-3">
          <div className="h-3 w-full bg-slate-100 rounded animate-pulse" />
          <div className="h-3 w-11/12 bg-slate-100 rounded animate-pulse" style={{ animationDelay: '75ms' }} />
          <div className="h-3 w-4/5 bg-slate-100 rounded animate-pulse" style={{ animationDelay: '150ms' }} />
          <div className="h-3 w-full bg-slate-100 rounded animate-pulse" style={{ animationDelay: '225ms' }} />
          <div className="h-3 w-3/5 bg-slate-100 rounded animate-pulse" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }

  if (!output) {
    return (
      <div className="border border-dashed border-slate-200 rounded-xl p-5 bg-slate-50/50 text-center">
        <p className="text-sm text-slate-500">
          Add events and build your appeal narrative to create payer-ready outputs.
        </p>
      </div>
    );
  }

  const tabs = [
    { id: "narrative", label: "Narrative" },
    { id: "bullets", label: "Bullet Timeline" },
    { id: "proofPoints", label: "Key Proof Points" },
  ] as const;

  const getActiveContent = () => {
    switch (activeTab) {
      case "narrative":
        return output.narrative;
      case "bullets":
        return output.bullets.map((b) => `• ${b}`).join("\n");
      case "proofPoints":
        return output.proofPoints.map((p, i) => `${i + 1}. ${p}`).join("\n");
    }
  };

  const hasWarnings = output.warnings && output.warnings.length > 0;

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      {/* ── Tab header ──────────────────────────────────── */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-1">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-xs font-medium transition-colors relative ${
                activeTab === tab.id
                  ? "text-slate-900"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
              )}
            </button>
          ))}
        </div>

        {/* Copy button */}
        <button
          onClick={() => handleCopy(getActiveContent(), activeTab)}
          className="mr-2 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors flex items-center gap-1"
        >
          {copiedSection === activeTab ? (
            <>
              <svg
                className="w-3.5 h-3.5 text-emerald-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>

      {/* ── Tips bar (amber dot pattern) ────────────────── */}
      {hasWarnings && (
        <div className="border-b border-slate-100">
          <button
            onClick={() => setWarningsCollapsed(!warningsCollapsed)}
            className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs bg-slate-50/80 hover:bg-slate-100/60 transition-colors"
          >
            <svg
              className="w-3.5 h-3.5 text-slate-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            <span className="text-slate-600 font-medium">
              Ways to strengthen
            </span>
            {/* Amber dot */}
            <span className="w-[7px] h-[7px] rounded-full bg-amber-400 flex-shrink-0" />
            <svg
              className={`w-3.5 h-3.5 text-slate-400 ml-auto transition-transform ${
                warningsCollapsed ? "" : "rotate-180"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {!warningsCollapsed && (
            <div className="px-3 py-2 bg-slate-50/30 text-xs text-slate-600 space-y-1">
              {output.warnings!.map((warning, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>{warning}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Content area ────────────────────────────────── */}
      <div className="p-4">
        {activeTab === "narrative" && (
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {output.narrative}
          </p>
        )}

        {activeTab === "bullets" && (
          <div className="space-y-0.5">
            {output.bullets.map((bullet, i) => {
              const { date, text } = parseBulletDate(bullet);
              const hasDate = date !== "—";
              return (
                <div
                  key={i}
                  className="flex items-start gap-2.5 py-1.5 text-sm"
                >
                  <span
                    className={`flex-shrink-0 w-[68px] text-right text-xs font-medium pt-0.5 ${
                      hasDate ? "text-slate-500" : "text-slate-300"
                    }`}
                  >
                    {date}
                  </span>
                  <span className="text-slate-700 leading-relaxed">{text}</span>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === "proofPoints" && (
          <ol className="space-y-2">
            {output.proofPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-700">{point}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* ── Footer ──────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 bg-slate-50">
        <span className="text-xs text-slate-400">
          {caseType ? CASE_TYPE_LABELS[caseType] : "Authorization request"}
        </span>
        {onInsertToAppeal && (
          <button
            onClick={() => onInsertToAppeal(activeTab)}
            className="px-2.5 py-1 text-xs text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded transition-colors font-medium"
          >
            Insert into Appeal →
          </button>
        )}
      </div>
    </div>
  );
};

export default TimelineOutput;